import { InfoOutlined } from '@mui/icons-material';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { BrightTypography, DarkTypography } from '../Typographies';

interface KeyValue {
  key: string;
  value: string;
  variant?: 'dark' | 'bright';
}

interface Example {
  input: string;
  result: ReactNode;
  contextInfo?: ReactNode;
}

interface Props {
  parameters: KeyValue[];
  examples: Example[];
  additionalInfo?: ReactNode | KeyValue[];
  description?: ReactNode;
}

const keyValueItem = (kv: KeyValue, key: number) => (
  <Stack key={key} direction={'row'} spacing={1} alignItems={'baseline'}>
    <Typography fontWeight={700}>{kv.key}</Typography>
    {kv.variant === 'dark' ? (
      <DarkTypography>{kv.value}</DarkTypography>
    ) : (
      <BrightTypography>{kv.value}</BrightTypography>
    )}
  </Stack>
);

const keyValueStack = (items: KeyValue[]) => (
  <Stack direction={'column'}>{items.map((item, idx) => keyValueItem(item, idx))}</Stack>
);

export const ExampleBox = ({ parameters, examples, additionalInfo, description }: Props): React.JSX.Element => {
  const descriptionElement = useMemo(() => description ?? <></>, [description]);

  const paramElement = useMemo(() => keyValueStack(parameters), [parameters]);

  const additionalElement = useMemo(() => {
    if (Array.isArray(additionalInfo)) {
      return keyValueStack(additionalInfo);
    }

    return additionalInfo ?? <></>;
  }, [additionalInfo]);

  const resultElement = useMemo(() => {
    const exampleElements = examples.map((ex, idx) => (
      <Stack key={idx} direction={'column'}>
        {ex.contextInfo && ex.contextInfo}
        <Stack direction={'row'} alignItems={'baseline'} sx={{ pl: 3 }}>
          <DarkTypography>{ex.input}</DarkTypography>
          <Typography sx={{ px: 1, fontWeight: 700, fontSize: '2rem' }}>→</Typography>
          {typeof ex.result === 'string' ? <BrightTypography>{ex.result}</BrightTypography> : ex.result}
        </Stack>
      </Stack>
    ));
    return <Stack direction={'column'}>{exampleElements}</Stack>;
  }, [examples]);

  return (
    <Alert variant={'outlined'} icon={<InfoOutlined sx={{ fontSize: 32 }} />} severity={'info'}>
      <Stack>
        {descriptionElement}
        <Typography variant={'button'} sx={{ fontWeight: 700, p: 2 }}>
          Example
        </Typography>
        <Box sx={{ p: 4 }}>{paramElement}</Box>
        {additionalElement}
        <Typography variant={'button'} sx={{ fontWeight: 700, p: 2 }}>
          Result
        </Typography>
        <Box sx={{ display: 'flex', p: 2 }}>{resultElement}</Box>
      </Stack>
    </Alert>
  );
};
